import { Injectable, inject } from '@angular/core';
import { ParentPlanIndicatorResponseDto } from 'src/generated-sources/openapi/configuration-service/plan-indicator';
import {
  AlertReportDetailDto,
  WalletRegistryAlertDto,
} from 'src/generated-sources/openapi/alert-service/alert';
import { RegistryTypeDto } from 'src/generated-sources/openapi/wallet-service/wallet';
import {
  AlertIndicatorVariation,
  FlattenedIndicator,
  RelationshipLevel,
} from '../models/wallet-alert-report.models';
import { IndicatorCategoryDto } from 'src/generated-sources/openapi/configuration-service/indicator';
import { IndicatorGroup } from 'src/app/model/configuration-service.models';
import {
  VIRTUAL_L1_PARENT_NAME,
  VIRTUAL_L2_PARENT_NAME,
} from '../models/wallet-alert-report.constants';
import { IndicatorDataService } from 'src/app/services/data/indicator-data.service';
import { groupPartnerAlerts } from '../alert-indicators-tags/alert-indicators-tags.utils';

@Injectable({ providedIn: 'root' })
export class AlertDataTransformerService {
  private readonly indicatorDataService = inject(IndicatorDataService);
  /**
   * Flattens parent-child indicator hierarchy and groups child indicators by indicator.group.
   *
   * Example: Parent "Restritivos" with children "Protestos - Valores" and "Protestos - Qtde" (both group="Protestos")
   * becomes: [{ name: "Restritivos", isParentIndicator: true }, { name: "Protestos", indicatorIds: ['2', '3'] }]
   */
  flattenIndicatorHierarchy(
    parentIndicators: ParentPlanIndicatorResponseDto[],
    walletType: RegistryTypeDto,
  ): FlattenedIndicator[] {
    const virtualGroups = parentIndicators.filter((g) => this.isVirtualGroup(g));
    const rootGroups = parentIndicators.filter((g) => !this.isVirtualGroup(g));
    const flattenedList = rootGroups.flatMap((g) => this.flattenRootGroup(g));

    if (virtualGroups.length > 0 && walletType === RegistryTypeDto.Company) {
      flattenedList.push(
        ...this.buildVirtualLevelEntries(
          VIRTUAL_L1_PARENT_NAME,
          RelationshipLevel.L1,
          parentIndicators,
        ),
        ...this.buildVirtualLevelEntries(
          VIRTUAL_L2_PARENT_NAME,
          RelationshipLevel.L2,
          parentIndicators,
        ),
      );
    }

    return flattenedList;
  }

  private flattenRootGroup(parentIndicator: ParentPlanIndicatorResponseDto): FlattenedIndicator[] {
    const entries: FlattenedIndicator[] = [
      {
        id: parentIndicator.id,
        name: parentIndicator.name,
        isParentIndicator: true,
        indicatorIds: [],
        relationshipLevel: RelationshipLevel.ROOT,
      },
    ];

    if (parentIndicator.planIndicators?.length > 0) {
      const groupMap = new Map<string, string[]>();

      for (const planIndicator of parentIndicator.planIndicators) {
        const indicator = planIndicator.indicator;
        const groupName = indicator.group || indicator.name;
        const ids = groupMap.get(groupName) ?? [];
        groupMap.set(groupName, ids);
        ids.push(indicator.id);
      }

      for (const [groupName, indicatorIds] of groupMap) {
        entries.push({
          id: indicatorIds.join('|'),
          name: groupName,
          isParentIndicator: false,
          indicatorIds,
          relationshipLevel: RelationshipLevel.ROOT,
        });
      }
    }

    return entries;
  }

  private buildVirtualLevelEntries(
    parentName: string,
    level: RelationshipLevel.L1 | RelationshipLevel.L2,
    parentIndicators: ParentPlanIndicatorResponseDto[],
  ): FlattenedIndicator[] {
    const groups = this.collectIndicatorGroupsForLevel(parentIndicators, level);
    if (groups.length === 0) return [];
    const entries: FlattenedIndicator[] = [
      {
        id: null,
        name: parentName,
        isParentIndicator: true,
        indicatorIds: [],
        relationshipLevel: level,
      },
    ];
    for (const { name, indicatorIds } of groups) {
      entries.push({
        id: indicatorIds.join('|'),
        name,
        isParentIndicator: false,
        indicatorIds,
        relationshipLevel: level,
      });
    }
    return entries;
  }

  private isVirtualGroup(group: ParentPlanIndicatorResponseDto): boolean {
    const levels = group.supportedRelationshipLevels ?? ['ROOT'];
    return !levels.includes('ROOT');
  }

  private collectIndicatorGroupsForLevel(
    parentIndicators: ParentPlanIndicatorResponseDto[],
    level: RelationshipLevel.L1 | RelationshipLevel.L2,
  ): { name: string; indicatorIds: string[] }[] {
    // Non-root (virtual) parent groups come first, then root parent groups.
    // Insertion order within each partition is preserved because parentIndicators
    // are pre-sorted by displayOrder before this method is called.
    const nonRootGroups = this.collectGroupsFromParents(
      parentIndicators.filter((p) => this.isVirtualGroup(p)),
      level,
    );
    const rootGroups = this.collectGroupsFromParents(
      parentIndicators.filter((p) => !this.isVirtualGroup(p)),
      level,
    );
    return [...nonRootGroups, ...rootGroups];
  }

  private collectGroupsFromParents(
    parentIndicators: ParentPlanIndicatorResponseDto[],
    level: RelationshipLevel.L1 | RelationshipLevel.L2,
  ): { name: string; indicatorIds: string[] }[] {
    const groupMap = new Map<string, string[]>();
    for (const parentIndicator of parentIndicators) {
      for (const planIndicator of parentIndicator.planIndicators ?? []) {
        const indicator = planIndicator.indicator;
        const supported: string[] = indicator.supportedRelationshipLevels ?? [];
        if (!supported.includes(level)) continue;
        const groupName = indicator.group || indicator.name;
        const ids = groupMap.get(groupName) ?? [];
        groupMap.set(groupName, ids);
        ids.push(indicator.id);
      }
    }
    return [...groupMap.entries()].map(([name, indicatorIds]) => ({ name, indicatorIds }));
  }

  /**
   * Builds the ordered _combinedAlerts array for a registry entry:
   * root alerts (sorted) → L1 shareholding → L1 partner alerts → L2 shareholding → L2 partner alerts
   */
  buildCombinedAlerts(registry: WalletRegistryAlertDto): AlertReportDetailDto[] {
    const sortedPartnerAlerts = this.indicatorDataService.sortByDisplayOrder(
      groupPartnerAlerts(registry.partnerAlerts ?? []),
      (a) => a.indicatorId,
    );
    const shareholdingAlerts = (registry.alerts ?? []).filter(
      (a) => a.indicatorGroup === IndicatorGroup.PERCENTUAL_QUADRO_SOCIETARIO,
    );
    const rootAlerts = this.indicatorDataService.sortByDisplayOrder(
      (registry.alerts ?? []).filter(
        (a) => a.indicatorGroup !== IndicatorGroup.PERCENTUAL_QUADRO_SOCIETARIO,
      ),
      (a) => a.indicatorId,
    );
    return [
      ...rootAlerts,
      ...shareholdingAlerts.filter((a) => a.relationshipLevel === RelationshipLevel.L1),
      ...sortedPartnerAlerts.filter((a) => a.relationshipLevel === RelationshipLevel.L1),
      ...shareholdingAlerts.filter((a) => a.relationshipLevel === RelationshipLevel.L2),
      ...sortedPartnerAlerts.filter((a) => a.relationshipLevel === RelationshipLevel.L2),
    ];
  }

  /**
   * Groups alerts by indicatorGroup, merging Value/Quantity categories.
   */
  mapToAlertIndicatorVariation(data: AlertReportDetailDto[]): AlertIndicatorVariation[] {
    if (!data || data.length === 0) {
      return [];
    }

    return Array.from(this.groupAlertsByIndicator(data).values());
  }

  private groupAlertsByIndicator(
    data: AlertReportDetailDto[],
  ): Map<string, AlertIndicatorVariation> {
    const groupedMap = new Map<string, AlertIndicatorVariation>();

    for (const item of data) {
      if (!this.isValidAlertCategory(item)) {
        continue;
      }

      const level = item.relationshipLevel as RelationshipLevel;
      const groupKey =
        level && level !== RelationshipLevel.ROOT
          ? `${item.indicatorGroup}:${level}`
          : item.indicatorGroup;

      if (!groupedMap.has(groupKey)) {
        groupedMap.set(groupKey, this.buildVariationEntry(item, level));
      }

      const groupedItem = groupedMap.get(groupKey);
      if (groupedItem && groupedItem.category !== IndicatorCategoryDto.Data) {
        this.mergeAlertValues(groupedItem, item);
      }
    }

    return groupedMap;
  }

  private buildVariationEntry(
    item: AlertReportDetailDto,
    level: RelationshipLevel,
  ): AlertIndicatorVariation {
    const base: AlertIndicatorVariation = {
      id: item.id,
      indicatorId: item.indicatorId,
      indicatorGroup: item.indicatorGroup,
      relationshipLevel: level,
      category: item.category,
    };

    const isPartnerData =
      item.category === IndicatorCategoryDto.Data && !!level && level !== RelationshipLevel.ROOT;
    if (!isPartnerData) {
      const isRootRegistrationData =
        item.category === IndicatorCategoryDto.Data &&
        item.indicatorGroup === IndicatorGroup.DADOS_CADASTRAIS;
      if (isRootRegistrationData) {
        return { ...base, isRegistrationData: true, currentValue: item.currentValue };
      }
      return base;
    }

    if (item.indicatorGroup === IndicatorGroup.PERCENTUAL_QUADRO_SOCIETARIO) {
      return { ...base, isPartnerShareholdingData: true, currentValue: item.currentValue };
    }

    return { ...base, isPartnerRegistrationData: true, currentValue: item.currentValue };
  }

  private isValidAlertCategory(alert: AlertReportDetailDto): boolean {
    const isNumeric =
      alert.category === IndicatorCategoryDto.Value ||
      alert.category === IndicatorCategoryDto.Quantity;

    if (isNumeric) return true;

    const isData = alert.category === IndicatorCategoryDto.Data;
    const isDadosCadastrais = alert.indicatorGroup === IndicatorGroup.DADOS_CADASTRAIS;
    const isPartnerLevel =
      !!alert.relationshipLevel && alert.relationshipLevel !== RelationshipLevel.ROOT;

    return isData && (isDadosCadastrais || isPartnerLevel);
  }

  private mergeAlertValues(
    groupedItem: AlertIndicatorVariation,
    alert: AlertReportDetailDto,
  ): void {
    if (alert.category === IndicatorCategoryDto.Value) {
      groupedItem.previousValue = String(
        Number(groupedItem.previousValue ?? 0) + Number(alert.previousValue ?? 0),
      );
      groupedItem.currentValue = String(
        Number(groupedItem.currentValue ?? 0) + Number(alert.currentValue),
      );
    } else if (alert.category === IndicatorCategoryDto.Quantity) {
      groupedItem.previousQuantity = String(
        Number(groupedItem.previousQuantity ?? 0) + Number(alert.previousValue ?? 0),
      );
      groupedItem.currentQuantity = String(
        Number(groupedItem.currentQuantity ?? 0) + Number(alert.currentValue),
      );
    }
  }

  parseShareholdingPartnerIds(currentValue: string | undefined): string[] {
    try {
      const parsed: { partnerId: string }[] = JSON.parse(currentValue ?? '[]');
      return [...new Set(parsed.map((p) => p.partnerId).filter(Boolean))];
    } catch {
      return [];
    }
  }

  collectTrueKeys(json: string | undefined): string[] {
    try {
      const parsed: Record<string, unknown> = JSON.parse(json ?? '{}');
      return Object.entries(parsed)
        .filter(([, val]) => val === true)
        .map(([key]) => key);
    } catch {
      return [];
    }
  }

  buildPartnersWithFlags(
    alerts: AlertReportDetailDto[],
  ): { registryId: string; document?: string; flags: string[] }[] {
    const partnersMap = new Map<
      string,
      { registryId: string; document?: string; flags: Set<string> }
    >();
    for (const a of alerts) {
      if (!a.registryId) continue;
      if (!partnersMap.has(a.registryId)) {
        partnersMap.set(a.registryId, {
          registryId: a.registryId,
          document: a.document ?? undefined,
          flags: new Set(),
        });
      }
      const entry = partnersMap.get(a.registryId);
      if (!entry) continue;
      if (!entry.document && a.document) {
        entry.document = a.document;
      }
      for (const key of this.collectTrueKeys(a.currentValue)) {
        entry.flags.add(key);
      }
    }
    return [...partnersMap.values()].map((p) => ({
      registryId: p.registryId,
      document: p.document,
      flags: [...p.flags],
    }));
  }
}
